/**
 * Theme foundation for Virgin AU site — framework-agnostic (no next/font imports).
 */
import type { Theme } from '@emotion/react';
import { globals } from 'common-ui';
import merge from 'lodash/merge';
import { icons } from 'common-font-icons';
import type { CommonThemeVars } from 'tsconfig/types.d';
import {
  buildThemeMixinsAPACLW,
  buildThemeWidgetStylesAPACLW,
  buildThemeVarsAPACLW,
} from 'dw-apac-law/theme-foundation';

type VIRFontFamilies = {
  montserrat?: string;
  gotham?: string;
  roboto?: string;
};

export const buildThemeVarsAPACVir = (vars: Pick<Theme, CommonThemeVars>, fontFamilies?: VIRFontFamilies) => {
  //primary
  const virCherryPie = '#2D054E';
  const virRed500 = '#E11931'; //color4
  const virRed600 = '#BB0C29'; //color5
  const virRed700 = '#FBE6F1'; //color6
  const virPurple100 = '#F0EAF3'; //color7
  const virPurple500 = '#6A288A'; //color9
  const virPurple700 = '#4A1C61'; //color10
  const virPurple800 = '#370A4C'; //color11
  const virPinkOfferPill = '#E11931';
  const virPurple600 = '#5E1F7A'; //color8
  //secondary
  const virCream300 = '#F8F5EF'; //color12
  const virCream500 = '#EFECE8'; //color13
  const virWhite = '#FFFFFF'; //color14
  const virGrey200 = '#EDEDED'; //color16
  const virGrey400 = '#BCBCBC'; //color17
  const virGrey600 = '#767676'; //color18
  const virGrey700 = '#616161'; //color19
  const virGrey800 = '#494949'; //color20
  const virBlack = '#222222'; //color21
  const virSecondaryDark = '#112B4B';
  const virSvgCheckboxBg = '%234A1C61';

  //accent-purple
  const virLavender = '#DCD4EA'; //color22
  const virLilac = '#977DC1'; //color23
  //accent-Green
  const virGreen = '#35B63E'; //color24
  const virTeal = '#469D8E'; //color25
  //accent-Gold
  const virGold = '#DFB158'; //color26
  const lwPink = '#CF004F';
  const virRed = '#D62021';

  const virOceanGreen = '#009F8E';
  const virOceanGreen700 = '#00797F';

  const montserrat = fontFamilies?.montserrat ?? 'Montserrat, sans-serif';
  const gotham = fontFamilies?.gotham ?? 'Gotham, sans-serif';
  const roboto = fontFamilies?.roboto ?? 'Roboto, sans-serif';

  const lwThemeVars = buildThemeVarsAPACLW(vars);

  return merge({}, lwThemeVars, {
    meta: {
      name: 'Virgin AU',
    },
    icons,
    assets: {
      headerLogoImgUrl: `${globals.assetsPath}/next/images/brand-logo.svg`,
      headerLogoMobileImgUrl: `${globals.assetsPath}/next/images/brand-logo.svg`,
      footerLogoImgUrl: `${globals.assetsPath}/next/images/brand-logo-white.svg`,
    },
    colors: {
      primary1: virRed500,
      primary2: virPurple500,
      primary3: virCream300,
      primary4: virRed500,
      primary5: virRed700,
      primary6: lwPink,
      primary7: virPurple100,
      primary8: virBlack,
      primary9: virPurple500,
      primary10: virPurple700,
      primary11: virPurple800,
      primary12: virPurple600,
      secondary1: virCream300,
      secondary2: virCream500,
      secondary3: virWhite,
      secondary4: virSecondaryDark,
      secondary5: virGrey200,
      secondary6: virGrey400,
      secondary7: virGrey600,
      secondary8: virGrey700,
      secondary9: virGrey800,
      secondary10: virBlack,
      secondary11: virOceanGreen,
      secondary12: virOceanGreen700,
      accentLavender: virLavender,
      accentLilac: virLilac,
      accentGreen: virGreen,
      accentTeal: virTeal,
      accentGold: virGold,
      accentThunderbird500: virRed,
      textPrimaryBg: virRed500,
      pinkOfferPillBg: virPinkOfferPill,
      svgCheckboxBg: virSvgCheckboxBg,
      //specific colour definitions
      btnsPrimaryBg: virRed500,
      btnsPrimaryColor: vars.colors.white,
      btnsPrimaryHoverBg: virRed600,
      btnsPrimaryHoverColor: vars.colors.white,
      btnsSecondaryBg: vars.colors.white,
      btnsSecondaryColor: vars.colors.black,
      btnsSecondaryHoverBg: virRed600,
      btnsSecondaryHoverColor: vars.colors.white,
      btnsTertiaryBg: vars.colors.white,
      btnsTertiaryColor: vars.colors.black,
      btnsTertiaryBorder: vars.colors.white,
      btnsTertiaryHoverBg: vars.colors.white,
      btnsTertiaryHoverColor: vars.colors.black,
      savingsTextColor: virRed500,

      footerPrimaryBg: virCherryPie,
      footerdrinkaware: vars.colors.white,

      bgColors: {
        virRed: virRed500,
        virPurple: virPurple500,
        virCream: virCream500,
        virWhite,
        virBlack,
      },
    },
    fonts: {
      familyPrimary: montserrat,
      familySecondary: roboto,
      familyTertiary: montserrat,
      familyTeritary: gotham,
      headerMainNavTitle: montserrat,
      headerMainNavSubTitle: montserrat,
      weightLight: '300',
      weightNormal: '400',
      weightMedium: '500',
      weightMediumPlus: '600',
      weightBold: '700',
    },
  });
};

export const buildThemeMixinsAPACVir = (vars: Omit<Theme, 'mixins' | 'widgets'>) => {
  const buildThemeWidget = buildThemeMixinsAPACLW(vars);

  return merge({}, buildThemeWidget, {
    hxl: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize2};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight100};
      letter-spacing: ${vars.fonts.fontLetterSpacing3};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize60};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight100};
      }
    `,
    h1: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize36};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight100};
      letter-spacing: ${vars.fonts.fontLetterSpacing3};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize48};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight100};
      }
    `,
    h2: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize32};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight105};
      letter-spacing: ${vars.fonts.fontLetterSpacing3};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize40};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight105};
      }
    `,
    h3: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize28};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight105};
      letter-spacing: ${vars.fonts.fontLetterSpacing4};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize36};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight105};
      }
    `,
    h4: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize24};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight110};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize30};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight110};
      }
    `,
    h5: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize22};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight110};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize26};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight110};
      }
    `,
    h6: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize20};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight115};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize24};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight115};
      }
    `,
    h7: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize18};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize22};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    h8: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize16};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize18};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    eyebrowRegular: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing7};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize14};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    eyebrowSmall: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize10};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing7};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize12};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    utility: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize10};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing9};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize12};
        line-height: ${vars.fonts.fontLineHeight100};
        letter-spacing: ${vars.fonts.fontLetterSpacing8};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    quote: `
      font-family: ${vars.fonts.familyTeritary};
      font-size: ${vars.fonts.fontSize20};
      font-weight: ${vars.fonts.weightLight};
      font-style: ${vars.fonts.styleItalic};
      line-height: ${vars.fonts.fontLineHeight110};
      letter-spacing: ${vars.fonts.fontLetterSpacing3};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize28};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight110};
      }
    `,
    body1: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize16};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    body2: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize14};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight130};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight130};
      }
    `,
    bodySmall: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight130};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight130};
      }
    `,
    caption: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
  });
};

export const buildThemeWidgetStylesAPACVirFromBase = (
  vars: Omit<Theme, 'widgets'>,
  themeStyles: Record<string, any>,
): ReturnType<typeof buildThemeWidgetStylesAPACLW> => {
  const overrides: Record<string, any> = {};

  if (themeStyles.GlobalHeader) {
    overrides.GlobalHeader = {
      headerLogo: `
      img {
        width: 32px;
        height: 28px;
        ${vars.breakpoints.sm} {
          width: 40px;
          height: 35px;
        }
        ${vars.breakpoints.lg} {
          width: 76px;
          height: 65px;
        }
      }`,
    };
  }

  if (themeStyles.Search) {
    overrides.Search = {
      hitWrapper: ` 
      ${themeStyles.Search?.hitWrapper ?? ''}
      &.grid-view {
        .ais-Hits-list {
          .ais-Hits-item {
          .hit-content {
            .title {
              font-family: ${vars.fonts.familyPrimary};
            }
          }
          .addToCart-container { 
            .price-section {
              .sku-price {
                .vpp-price-section {
                  font-family: ${vars.fonts.familySecondary};
                }
              }
              .member-price-section-case {
                font-size: ${vars.fonts.sizeDefault};
              }
            }
          }
        }
      }
      `,
    };
  }

  if (themeStyles.Product) {
    overrides.Product = {
      wrapper: `  
        ${themeStyles.Product?.wrapper ?? ''}
        .layout-name {
          .sub-title {
            font-family: ${vars.fonts.familySecondary};
          }
        }
      `,
      orderform: `
        ${themeStyles.Product?.orderform ?? ''}
        .top-price-section {
          .price-section {
            .case-sku-savings-sales-price {
              font-family: ${vars.fonts.familyPrimary} !important;
            }
          }
        }
        .straight-sku-container {
          .vpp-price {
            .price {
              font-family: ${vars.fonts.familyPrimary} !important;
            }
          }
        }
        .top-price-section {
          .price-section-sku {
            .price {
                font-family: ${vars.fonts.familyPrimary} !important;
            }
          }
        }
      `,
      learnMoreWrapper: `
      ${themeStyles.Product?.learnMoreWrapper ?? ''}
      .modalTitle {
        font-family: ${vars.fonts.oswald} !important;
        font-size: 28px !important;
      }
      .modalTitle2 {
        font-family: ${vars.fonts.noto} !important;
      }
    `,
    };
  }

  if (themeStyles.Footer) {
    overrides.Footer = {
      default: `
      ${themeStyles.Footer?.default ?? ''}
      font-family: ${vars.fonts.familySecondary};
      p {
        font-family: ${vars.fonts.familySecondary};
      }
      @media print {
        display: none;
      }
      .footer-bottom-section, .social-connections {
        background: ${vars.colors.footerPrimaryBg};
        box-shadow: 0 0 0 100vmax ${vars.colors.footerPrimaryBg};
        clip-path: inset(0 -100vmax);
        color: ${vars.colors.white};
        .wrapper {
          .copyrights {
            p {
              color: ${vars.colors.white};
              a {
                color: ${vars.colors.white};
              }
            }
          }
          .brand img {
              max-width: 6.5em;
          }
          ${vars.breakpoints.lg} {
            .bedrinkaware-group {
              justify-content: flex-start;
            }
          }
        }
      }
      .footer-nav-links {
        background: ${vars.colors.secondary1};
        box-shadow: 0 0 0 100vmax ${vars.colors.secondary1};
      }
      .social-connections {
        .wrapper {
          .social-connection-heading p {
            font-family: ${vars.fonts.familySecondary};
            letter-spacing: 2px;
            color: ${vars.colors.white};
          }
          a {
            color: ${vars.colors.white};
          }
        }
      }
      `,
      navItems: `
        ${themeStyles.Footer?.navItems ?? ''}
        > div:first-of-type p{
          text-transform: uppercase;
          font-weight: bold;
        }
  `,
    };
  }

  if (themeStyles.Cart) {
    overrides.Cart = {
      orderSummaryContainer: `
        ${themeStyles.Cart?.orderSummaryContainer ?? ''}
        .cart-head {
          h2 {
              font-weight: ${vars.fonts.weightMediumPlus} !important;
          }
        }

        .cart-area .empty-basket  h3 {
          font-weight: ${vars.fonts.weightMediumPlus};
        }
      `,
      MiniCart: `
      ${themeStyles.Cart?.MiniCart ?? ''}
      .mini-cart-header {
        h3{
          font-weight: ${vars.fonts.weightMediumPlus};
        }
      }

      .price-section {
        ${vars.breakpoints.md}{ 
          flex-flow: column;
          align-items: baseline;
        }
      }

      .price-section .strike+.sale-price {
        color: ${vars.colors.accentThunderbird500}
      }

      .price-section .strike+.member-price {
        color:${vars.colors.secondary11};
      }

      .member-savings {
        color: ${vars.colors.secondary11};
      }
      `,
      cartDetailSection: `
        ${themeStyles.Cart?.cartDetailSection ?? ''}
        .product-link {
          font-weight: ${vars.fonts.weightMediumPlus};
          color: ${vars.colors.primary8};
        }
      `,
      cartLayout: {
        cartCounterAndPrice: `
        ${themeStyles.Cart?.cartLayout?.cartCounterAndPrice ?? ''}
          .price-savings h6 {
            font-weight: ${vars.fonts.weightMediumPlus};
          }
        `,
      },
      EDWrapper: `
       ${themeStyles.Cart?.EDWrapper ?? ''}
        .wrapper-container h2 , .order-total {
          font-weight: ${vars.fonts.weightMediumPlus};
        }
      `,
    };
  }

  // CmsTBSPage is unconditional — always applied
  overrides.CmsTBSPage = {
    sideBarLegacy: `
    & .sidebar {
      & .grid-container {
        div {
          width: 100%;
          .navigation-link {
            h4 {
              display: none; 
            }
            & a {
              margin-left: 10px;
              margin-bottom: 12px;
              display: flex;
              align-items: center;
              gap: 10px;
              font-family: ${vars.fonts.familyPrimary};
              font-size: ${vars.fonts.fontSize14};
              font-weight: ${vars.fonts.weightNormal};
              line-height: 22.4px;
              letter-spacing: 0.04em;
              color: ${vars.colors.grey50};
              &:last-child {
                & i {
                  font-size: unset;
                }
              }
            }
          }
        }
      }
      & .cms-conditional-container {
        margin-left: -24px;
        & .grid-container > div {
          display: flex;
          align-items: center;
          padding-bottom: 5px;
          line-height: 25.2px;
          letter-spacing: 0.04em;
          gap: 10px;
          margin-bottom: 10px;
          .cms-image-container {
            display: flex;
            width: fit-content;
            img {
              width: 25px;
            }
          }
          a {
            font-family: ${vars.fonts.familyPrimary};
            font-size: ${vars.fonts.fontSize16};
            font-weight: ${vars.fonts.weightBold};
            color: ${vars.colors.grey50};
            margin: unset;
          }
        }
      }
    }
  `,
    breadcrumbCSS: `
    padding: 12px 24px 12px 0px;
    & a {
      display: flex;
      align-items: center;
      gap: 8px;
      &:hover, &:active, &:focus {
        text-decoration: none;
      }
      & .link-text {
        text-decoration: none;
        border-bottom: 1px solid ${vars.colors.primary8};
        font-size: ${vars.fonts.sizeH5};
        font-weight: bold;
      }
    }
  `,
  };

  if (themeStyles.Membership) {
    overrides.Membership = {
      pageWrapper: `
          ${themeStyles.Membership?.pageWrapper ?? ''}
          & .sidebar{
            display: none;
            ${vars.breakpoints.md} {
               display:flex;
            }
            .grid-container div .navigation-link a.active {
              color: ${vars.colors.primary12};
            }
          }
           .intro-text{
              ${vars.mixins.h6}
              font-size: ${vars.fonts.sizeLarger};
              text-align: center;
              margin-top: 16px;
              margin-bottom: 16px;
              line-height: 27.6px;
              ${vars.breakpoints.sm} {
                font-size: ${vars.fonts.sizeH6};
              }
           }
          & .top-section{
              .dynamicComponents{
                >.widget-container{
                 .cms-image-container{
                    background: ${vars.colors.whiteWine};
                    display:flex;
                    justify-content:center;
                    img{
                      padding:10px;
                      width:300px;
                    }
                  }
                }
                >.widget-cms-column{
                  & .grid-container {
                        justify-content: space-between;
                        & .cms-image-container img{
                          width:88px;
    
                        }
    
                  }
                }
              }
            }
            & .bottom-section{
              & .accordion-item{
                 padding: 15px 0px;
                 
                .accordion-title h3{
                    font-size: ${vars.fonts.fontSize18};
                    font-family: ${vars.fonts.familyPrimary};
                }
                .accordion-content p{
                 font-family: ${vars.fonts.familyPrimary};
    
                }

              }
                & .banner-button a{
                background: ${vars.colors.secondary11};
                :hover {
                background-color: ${vars.colors.secondary12};
                
              }
                }
            }
            & .cancel-link-monthly-notes{
                ${vars.mixins.body1};
                 background-color: ${vars.colors.secondaryLight};
                 padding: 10px;
                 margin-bottom:5px;
                 i{
                   margin-right:5px;
                 }
    
            }
            & .cancel-membership-link{
                ${vars.mixins.clearRefinements};
                 padding: 0 !important;
            }
            & button.cancel-link-primary-cta{
                background-color:${vars.colors.textUnlimitedBg};
                line-height: initial;
                font-weight: ${vars.fonts.weightSemiBold};
                width: fit-content;
                order: 1;
                &:hover{
                  background-color: ${vars.colors.textUnlimitedHoverBg};
               }
               ${vars.breakpoints.sm} {
                order: initial;
              }
            }
            & .cancel-link-primary-text {
                ${vars.mixins.body1};         
            }
            & .renew-section {
              display: flex;
              gap: 16px;     
              flex-direction: column;
              ${vars.breakpoints.sm} {
                flex-direction: row;
                align-items: center;
              }
            }

          `,
    };
  }

  if (themeStyles.MembershipRebranding) {
    overrides.MembershipRebranding = {
      membershipStatus: `
          ${themeStyles.MembershipRebranding?.membershipStatus ?? ''}
            border: 1px solid ${vars.colors.lwGrey400};
            padding: 24px;
             h1.membership-status {
               font-family: ${vars.fonts.familyPrimary};
              color: ${vars.colors.primary2};
               font-size: ${vars.fonts.fontSize48};
              font-weight: ${vars.fonts.weightMediumPlus};
            }
            .re-join-note{
               margin-top: 12px;
            }
            .year-note {
              font-weight: ${vars.fonts.weightBold};
              font-family: ${vars.fonts.roboto};
              color: ${vars.colors.unltPopupBackground};
           
            }
            .add-unlimited-to-cart button{
               margin-top: 20px;
              background: ${vars.colors.unltPopupBackground};
              :hover{
               background-color: ${(vars.colors.bgColors as unknown as { [key: string]: string }).LWDarkBlue};
               color: ${vars.colors.white};
                
              }
            }
            .saving-instruction {
              font-family: ${vars.fonts.roboto};
              padding-top: 10px;
              font-weight: ${vars.fonts.weightNormal};
              font-size: ${vars.fonts.sizeLarge};
            }
            .saving-note {
              font-family: ${vars.fonts.familyPrimary};
              font-weight: ${vars.fonts.weightNormal};
              font-size: ${vars.fonts.fontSize14};
              color: ${vars.colors.grey60};
              margin-top: 12px;
            }
            .price-section {
              display: flex;
              align-items: baseline;
            }
            .membership-status-title {
              font-family: ${vars.fonts.familyPrimary};
              font-weight: ${vars.fonts.weightNormal};

            }
            .membership-subscription-title {
              font-family: ${vars.fonts.familyPrimary};
              font-weight: 700;
              font-size: ${vars.fonts.sizeLarge};
              padding-top: 10px;
              .monthly-notes{
                ${vars.mixins.body2};
                i{
                  margin:0px 10px;
                }
              }
            }
            .enroll-info {
              margin-top: 12px;
              font-family: ${vars.fonts.roboto};
              font-weight: ${vars.fonts.weightBold};
              font-size: ${vars.fonts.sizeLarge};
              & .date {
                font-weight: ${vars.fonts.weightNormal};
                font-size: ${vars.fonts.sizeLarge};
                padding-right: 8px;
              }
            }
          `,
      payment: `
        margin-top: 24px;
        ${vars.breakpoints.sm} {            
          margin-top: 40px;
        }
        .payment-title{
          ${vars.mixins.h6}
          font-family: ${vars.fonts.familyTertiary};
          font-weight: ${vars.fonts.weightNormal};
        }
        .last-payment-row{
          display: flex;
          font-family: ${vars.fonts.familyPrimary};
          font-size: ${vars.fonts.sizeLarge};
          color:${vars.colors.primary8};
          gap: 4px;
        }
        .last-payment-title{
          font-weight: ${vars.fonts.weightNormal};
          padding-right: 4px;
        }
        .payment-date{
          font-weight: ${vars.fonts.weightBold};
          .loaderContainer div {
            width: 20px;
            height: 20px;
            border-width: 2px;
          }
        }
      `,
      layout: `
             ${themeStyles.MembershipRebranding?.layout ?? ''}
             & .change-payment-card a{
               font-family: ${vars.fonts.roboto};
               text-decoration: underline;
               font-size: ${vars.fonts.fontSize14} !important;
               }
                & .renew-layout {
              & .update-visible {
                display: block;

              button.unlimited-modal-primary-cta {
              cursor: pointer;
              width: 100%;
              background-color: ${vars.colors.secondary11};
              color: ${vars.colors.secondary3};
              ${vars.breakpoints.sm} {
                width: auto;
              }

              :hover {
                background-color: ${vars.colors.secondary12};
                color: ${vars.colors.secondary3};
              }
              }
            }
          }
           `,
      membershipOffers: `
       ${themeStyles.MembershipRebranding?.membershipOffers ?? ''}
         .membership-offers-subtitle {
           font-family: ${vars.fonts.familyPrimary};
              color: ${vars.colors.primary2};
              font-size: ${vars.fonts.fontSize48};
              font-weight: ${vars.fonts.weightMediumPlus};
        }
       `,
    };
  }

  if (themeStyles.MyAccount) {
    overrides.MyAccount = {
      unlimitedModalWrapper: `
           ${themeStyles.MyAccount?.unlimitedModalWrapper ?? ''}
            & .mantine-Modal-title{
              font-weight: ${vars.fonts.weightMedium};
            }
           .unlimited-modal-content-wrapper{
              gap:20px;
             
              .mantine-Text-root{
                  font-family: ${vars.fonts.familyPrimary};
                  font-size: ${vars.fonts.fontSize16};
                  font-weight: ${vars.fonts.weightNormal};
              }
              .swap-note{
                 ${vars.mixins.body1};
                
              }
              .heading-notes{
                font-size: ${vars.fonts.fontSize16};
                }
              }
             .unlimited-icons{
               i {
                color:${vars.colors.secondary11} !important;
                font-size: 50px;
                background: ${vars.colors.whiteWine};
                border-radius: 100%;
               }
               .notes .notes-heading{
                 ${vars.mixins.eyebrowRegular};
                 ${vars.breakpoints.sm} 
                 {
                  font-size: ${vars.fonts.fontSize12};
                 }
    
                }
             }
             button.unlimited-modal-tertiary-cta {
              font-weight: ${vars.fonts.weightSemiBold};
             }
             button.unlimited-modal-primary-cta{
              background-color: ${vars.colors.textUnlimitedBg};
              min-width:200px;
              font-weight: ${vars.fonts.weightSemiBold};
               &[disabled]{
                 cursor: not-allowed;
                 pointer-events:none;
                //  background-color:${vars.colors.grey00};
    
               }
              &:hover{
                background-color: ${vars.colors.textUnlimitedHoverBg};
              }
             }
             & .monthly-offer-box{
              .radio-offer{
                 border: 1px solid ${vars.colors.lwGrey400};
                 margin:15px;
                 padding:10px;
                &.active{
                   border: 1.5px solid ${vars.colors.lwBodyBlack};
                }
                & input:checked:not(:disabled) {
                    background-color: transparent;
                    border: 2px solid ${vars.colors.btnsPrimaryBg};
                    ~ svg{
                      color: ${vars.colors.btnsPrimaryBg};
                    }
                  }   
              
                label{
                    ${vars.mixins.body1};
                     font-weight: ${vars.fonts.weightBold};
                }
                .badge-row {
                  display: flex;
                   justify-content: space-between;
                  .mantine-Badge-root{
                    ${vars.mixins.body3};
                        font-weight: ${vars.fonts.weightMediumPlus};
                    background-color: ${vars.colors.positiveDark};
                    color:${vars.colors.white};
                    text-transform: capitalize;
                    padding:10px;
                   }
                  }
                  .offer-notes{
                    margin-left: 5%;
                    ${vars.mixins.body2};
                    margin-top: 12px;
                  }
              }
             }
        `,
    };
  }

  return merge({}, themeStyles, overrides) as ReturnType<typeof buildThemeWidgetStylesAPACLW>;
};

export const buildThemeWidgetStylesAPACVir = (vars: Omit<Theme, 'widgets'>) =>
  buildThemeWidgetStylesAPACVirFromBase(vars, buildThemeWidgetStylesAPACLW(vars));

export { buildThemeWidgetStylesAPACLWFromBase } from 'dw-apac-law/theme-foundation';

export {
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from 'dw-apac-law/theme-foundation';
